/* Dependency Imports */
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';

/* Project Imports */
import { useProjectQuery } from '../../features/project/projectHooks';
import { useUserQuery } from '../../features/auth/authHooks';
import { selectUser } from '../../features/auth/authSlice';
import { selectProject } from '../../features/project/projectSlice';
import LoginPopup from '../login/LoginPopup';
import PortalPasswordScreen from './PortalPasswordScreen';
import { isPortalUnlocked } from '../../utils/portalAccess';
import { urlName, hyphenToTitle } from '../../utils/Functions';

type PortalAccess = 'grant' | 'login' | 'password';

const RequireAuth = () => {
  /* Redux */
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { project } = useSelector(selectProject);
  const { projectName, scheduleid } = useParams();
  const { loading, data } = useProjectQuery(projectName!);
  useUserQuery();
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (project?._id) {
      setUnlocked(isPortalUnlocked(project._id));
    }
  }, [project?._id]);

  const portalActive = project?.portal?.active || unlocked;

  const checkPortalAccess = (): PortalAccess => {
    if (hyphenToTitle(location.pathname.split('/')[1]).toLocaleLowerCase() === project.name.toLocaleLowerCase()) {
      if (portalActive) {
        return 'grant';
      }
      if (project.portal.vipActive) {
        return 'login';
      }
      return 'password';
    }
    if (location.pathname.split('/')[3] === 'vip' && project.portal.vipActive) {
      return user ? 'grant' : 'login';
    }
    if (location.pathname.split('/')[3] === 'vip' && !project.portal.vipActive) {
      if (portalActive) {
        navigate(`/${urlName(project.developerName)}/${urlName(project.name)}/public`);
        return 'grant';
      }
      return 'password';
    }
    const isPublicSegment = location.pathname.split('/')[3] === 'public' || location.pathname.split('/')[3] === 'private';

    if (isPublicSegment && portalActive) {
      return 'grant';
    }
    if (isPublicSegment && !portalActive && scheduleid) {
      return 'grant';
    }
    if (
      isPublicSegment &&
      !portalActive &&
      project.portal.createWorksheets &&
      location.pathname.includes('create-worksheets')
    ) {
      return 'grant';
    }
    if (isPublicSegment && !portalActive) {
      return 'password';
    }
    return 'grant';
  };

  if (loading) {
    return null;
  }

  // No matching project (bad URL) or the query failed - send them home instead of a blank page
  if (!data?.projectOne) {
    return <Navigate to="/" replace />;
  }

  if (!project?._id) {
    return null;
  }

  const access = checkPortalAccess();

  if (access === 'grant') {
    return <Outlet />;
  }

  return (
    <Box sx={{ backgroundColor: '#002044' }}>
      {access === 'password' ? (
        <PortalPasswordScreen project={project} onUnlock={() => setUnlocked(true)} />
      ) : (
        <LoginPopup project={project._id} />
      )}
    </Box>
  );
};

export default RequireAuth;
